import { useState } from "react";
import { analyzeStock } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Sparkles, TrendingUp, TrendingDown, Minus, AlertTriangle, Target } from "lucide-react";

export default function AIPanel({ symbol, stockName }) {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAnalyze = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await analyzeStock(symbol);
      setAnalysis(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Analysis failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const recIcon = {
    BUY: <TrendingUp className="w-5 h-5" />,
    SELL: <TrendingDown className="w-5 h-5" />,
    HOLD: <Minus className="w-5 h-5" />,
  };

  const recClass = {
    BUY: "badge-buy",
    SELL: "badge-sell",
    HOLD: "badge-hold",
  };

  if (!analysis && !loading) {
    return (
      <div className="ai-glow p-6 md:p-8 animate-pulse-gold" data-testid="ai-panel-idle">
        <div className="flex items-center gap-3 mb-4">
          <Sparkles className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
          <h3 className="font-heading text-xl font-medium text-bt-text-primary">AI Investment Analysis</h3>
        </div>
        <p className="text-sm text-bt-text-secondary mb-6 font-body leading-relaxed">
          Get an AI-powered analysis of {stockName || symbol} with buy/sell/hold recommendation, confidence score, and risk assessment.
        </p>
        {error && (
          <div className="flex items-center gap-2 text-bt-negative text-sm mb-4" data-testid="ai-error">
            <AlertTriangle className="w-4 h-4" />
            {error}
          </div>
        )}
        <Button
          onClick={handleAnalyze}
          className="bg-bt-gold text-bt-bg hover:bg-bt-gold-light font-body font-medium"
          data-testid="ai-analyze-btn"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Analyze with AI
        </Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="ai-glow p-6 md:p-8" data-testid="ai-panel-loading">
        <div className="flex items-center gap-3 mb-6">
          <Sparkles className="w-5 h-5 text-bt-gold animate-spin" strokeWidth={1.5} />
          <h3 className="font-heading text-xl font-medium text-bt-text-primary">Analyzing {symbol}...</h3>
        </div>
        <div className="space-y-3">
          <Skeleton className="h-8 w-32 bg-bt-surface-2" />
          <Skeleton className="h-4 w-full bg-bt-surface-2" />
          <Skeleton className="h-4 w-3/4 bg-bt-surface-2" />
          <Skeleton className="h-4 w-5/6 bg-bt-surface-2" />
        </div>
      </div>
    );
  }

  const a = analysis?.analysis || {};

  return (
    <div className="ai-glow p-6 md:p-8" data-testid="ai-panel-result">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Sparkles className="w-5 h-5 text-bt-gold" strokeWidth={1.5} />
          <h3 className="font-heading text-xl font-medium text-bt-text-primary">AI Analysis</h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleAnalyze}
          className="text-bt-text-secondary hover:text-bt-gold text-xs font-mono"
          data-testid="ai-refresh-btn"
        >
          Refresh
        </Button>
      </div>

      {/* Recommendation Badge */}
      <div className="flex items-center gap-4 mb-6">
        <div className={`inline-flex items-center gap-2 px-4 py-2 font-mono text-lg font-bold ${recClass[a.recommendation] || "badge-hold"}`} data-testid="ai-recommendation">
          {recIcon[a.recommendation]}
          {a.recommendation || "HOLD"}
        </div>
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary">Confidence</p>
          <p className="font-mono text-2xl font-medium text-bt-text-primary" data-testid="ai-confidence">{a.confidence || 0}%</p>
        </div>
      </div>

      {/* Summary */}
      {a.summary && (
        <p className="font-heading text-base italic text-bt-text-primary mb-6 leading-relaxed" data-testid="ai-summary">
          "{a.summary}"
        </p>
      )}

      {/* Reasons */}
      {a.reasons && a.reasons.length > 0 && (
        <div className="mb-6">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-secondary mb-3">Key Reasons</p>
          <ul className="space-y-2">
            {a.reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-bt-text-secondary font-body" data-testid={`ai-reason-${i}`}>
                <span className="w-1 h-1 mt-2 bg-bt-gold rounded-full shrink-0" />
                {r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Risk & Target */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-bt-surface-2 p-3 border border-bt-border-subtle">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary mb-1">Risk Level</p>
          <div className="flex items-center gap-2" data-testid="ai-risk">
            <AlertTriangle className={`w-4 h-4 ${a.risk === "High" ? "text-bt-negative" : a.risk === "Low" ? "text-bt-positive" : "text-bt-gold"}`} />
            <span className="font-mono text-sm font-medium text-bt-text-primary">{a.risk || "Medium"}</span>
          </div>
        </div>
        <div className="bg-bt-surface-2 p-3 border border-bt-border-subtle">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-bt-text-tertiary mb-1">Target Range</p>
          <div className="flex items-center gap-1" data-testid="ai-target">
            <Target className="w-4 h-4 text-bt-gold" />
            <span className="font-mono text-sm font-medium text-bt-text-primary">
              ${a.target_low?.toLocaleString()} - ${a.target_high?.toLocaleString()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
